import { useState, useCallback, useMemo, useRef, useContext } from 'react'
import PropTypes from 'prop-types'

import { convertTraits } from '@aytm/app/stats/lib/query/traits'
import { DEFAULT_SELECTED_QUESTION } from '@aytm/app/stats/page/comments/lib/constants'
import { hasVirtualOrXpert, getQuestionsOptionsList } from '@aytm/app/stats/page/comments/lib/utils'

import { OEContext } from '@aytm/app/stats/page/comments'

import SurveyHeaderComments from '@aytm/app/stats/page/comments/comments/components/SurveyHeaderComments'
import PageDataComments from '@aytm/app/stats/page/comments/comments/PageDataComments'
import PageDataQA from '@aytm/app/stats/page/comments/comments/PageDataQA'
import useFetchDataComments from '@aytm/app/stats/page/comments/comments/hooks/useFetchDataComments'
import useComments from '@aytm/app/stats/page/comments/comments/hooks/useComments'
import useTags from '@aytm/app/stats/page/comments/comments/hooks/useTags'
import useCodeBooks from '@aytm/app/stats/page/comments/comments/hooks/useCodeBooks'

import useEventListener from '@aytm/app/stats/page/components/hooks/useEventListener'

// styles
import '@aytm/app/stats/page/comments/styles/keyframes.scss'

import style from '@aytm/app/stats/page/comments/styles/index.module.scss'

const CommentsOpenEnded = props => {
  const {
    specialMode,
    surveyData,
    reportId,
    apiKey,
    sentimentOptions,
    reportParams = {},
    reportVirtual,
    reportSchema,
    loadRejectedCS,
    isShowHiddenFilters,
    isAdvancedFiltersDisabled,
    isSpecialsSortingDisabled,
    onFetch,
    onTagsFetch,
    onUpdateSchema,
    onUpdateParams,
    onFetchSchema,
    onFetchApiKey,
    onCreateFilter,
    onTranslationChange,
    onQuestionsListChange,
    onSelectedQuestionChange,
    onQAModeChange
  } = props

  const { searchState, translation, dispatch, dispatchCommentStatuses } = useContext(OEContext)

  const { pageData = [], tagsData, request, disabled: readOnly, isQAMode, isQALoading, isShowSentiments } = searchState

  const [popupNotificationsDisabled, setPopupNotificationsDisabled] = useState({
    kudos: 'true' == String(window.localStorage.getItem('kudosNotificationsDisabled')) || false,
    unkudos: 'true' == String(window.localStorage.getItem('unkudosNotificationsDisabled')) || false,
    reject: 'true' == String(window.localStorage.getItem('rejectNotificationsDisabled')) || false
  })

  const [survey, setSurvey] = useState(
    Object.assign({}, surveyData.survey || {}, {
      traits: convertTraits(surveyData.survey || {}),
      codeBooks: (surveyData.survey || {}).codeBooks || []
    })
  )
  const [responsesOptions, setResponsesOptions] = useState({
    survey,
    reportParams,
    reportVirtual,
    reportSchema,
    selectedQuestion: reportParams.selectedQuestion || DEFAULT_SELECTED_QUESTION
  })

  const responsesRef = useRef([])
  const showTraitsRef = useRef({})
  const responseOrderRef = useRef({})
  const currentResponseRef = useRef()

  const searchStateRef = useRef()
  searchStateRef.current = searchState

  const responsesOptionsRef = useRef()
  responsesOptionsRef.current = responsesOptions

  const propsRef = useRef()
  propsRef.current = props
  const surveyRef = useRef()
  surveyRef.current = survey

  const isSpecialsSortingDisabledRef = useRef()
  isSpecialsSortingDisabledRef.current = isSpecialsSortingDisabled

  const questionsList = useMemo(
    () =>
      getQuestionsOptionsList(
        responsesOptions.survey,
        responsesOptions.reportVirtual,
        responsesOptions.reportSchema,
        responsesOptions.selectedQuestion,
        specialMode
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      responsesOptions.survey.questions,
      responsesOptions.survey.scripts,
      responsesOptions.reportVirtual,
      responsesOptions.reportSchema,
      responsesOptions.selectedQuestion,
      specialMode
    ]
  )

  const PageDataComponent = useMemo(
    () => (isQAMode && !isQALoading ? PageDataQA : PageDataComments),
    [isQAMode, isQALoading]
  )

  const [isVirtualOrXpertInScope, isOnlyVirtualOrXpert] = hasVirtualOrXpert(questionsList, request.selectedQuestion)

  const onPageChange = (page, uidsList = null) => {
    const _request = {
      currentPage: page
    }
    if (uidsList) {
      _request.uidsToApprove = uidsList
    }

    dispatch({ type: 'SET_PARAM', request: { name: 'isPreserveState', value: true } })
    dispatch({
      type: 'SET_REQUEST_PARAM_SET',
      request: _request
    })
  }

  const onCloseNotification = useCallback(
    type => {
      setPopupNotificationsDisabled({
        ...popupNotificationsDisabled,
        [type]: true
      })
      window.localStorage.setItem(`${type}NotificationsDisabled`, true)
    },
    [popupNotificationsDisabled]
  )

  const { fetchData, fetchDataDelay } = useFetchDataComments({
    apiKey,
    dispatch,
    dispatchCommentStatuses,
    searchStateRef,
    propsRef,
    responsesOptionsRef,
    surveyData,
    survey,
    translation,
    specialMode,
    isVirtualOrXpertInScope,
    isOnlyVirtualOrXpert,
    setSurvey,
    onFetchSchema,
    onFetch,
    onFetchApiKey,
    onQAModeChange
  })

  useComments({
    dispatch,
    searchStateRef,
    propsRef,
    responsesOptionsRef,
    surveyData,
    survey,
    specialMode,
    sentimentOptions,
    questionsList,
    isShowSentiments,
    isSpecialsSortingDisabledRef,
    reportParams,
    responsesRef,
    reportVirtual,
    reportSchema,
    pageData,
    setSurvey,
    setResponsesOptions,
    onUpdateSchema,
    onQuestionsListChange
  })

  const [_onTagsUpdateSchema, _onTagsFetch] = useTags({
    survey,
    tagsData,
    request,
    dispatch,
    setSurvey,
    onFetch: fetchData,
    onTagsFetch,
    onUpdateSchema
  })

  const [_onCodeBooksFetch] = useCodeBooks({
    survey,
    tagsData,
    request,
    dispatch,
    setSurvey,
    onFetch: fetchData,
    onTagsFetch
  })

  useEventListener('tagsFetch', event => {
    if (event.detail) {
      const { data, updated } = event.detail
      _onTagsFetch(data, updated)
    }
  })

  useEventListener('codeBooksFetch', event => {
    if (event.detail) {
      const { data, updated } = event.detail
      _onCodeBooksFetch(data, updated)
    }
  })

  return (
    <>
      <div className={`${style['comments-container']} stats-Comments`}>
        <SurveyHeaderComments
          survey={survey}
          readOnly={readOnly}
          specialMode={specialMode}
          pageData={responsesOptions.pageData}
          questionsList={questionsList}
          showTraitsRef={showTraitsRef}
          responseOrderRef={responseOrderRef}
          currentResponse={currentResponseRef}
          loadRejectedCS={loadRejectedCS}
          isVirtualOrXpertInScope={isVirtualOrXpertInScope}
          isOnlyVirtualOrXpert={isOnlyVirtualOrXpert}
          isAdvancedFiltersDisabled={isAdvancedFiltersDisabled}
          isSpecialsSortingDisabled={isSpecialsSortingDisabled}
          onUpdateSchema={_onTagsUpdateSchema}
          onCreateFilter={onCreateFilter}
          onTranslationChange={onTranslationChange}
          onSelectedQuestionChange={onSelectedQuestionChange}
          onPageChange={onPageChange}
          fetchData={fetchData}
          fetchDataDelay={fetchDataDelay}
          onFetchApiKey={onFetchApiKey}
        />
        <PageDataComponent
          reportId={reportId}
          apiKey={apiKey}
          survey={survey}
          responsesOptions={responsesOptions}
          setResponsesOptions={setResponsesOptions}
          responsesRef={responsesRef}
          showTraitsRef={showTraitsRef}
          responseOrderRef={responseOrderRef}
          currentResponse={currentResponseRef}
          isVirtualOrXpertInScope={isVirtualOrXpertInScope}
          isOnlyVirtualOrXpert={isOnlyVirtualOrXpert}
          isShowHiddenFilters={isShowHiddenFilters}
          popupNotificationsDisabled={popupNotificationsDisabled}
          setCurrentResponse={responseData => (currentResponseRef.current = responseData)}
          onUpdateSchema={_onTagsUpdateSchema}
          onUpdateParams={onUpdateParams}
          onPageChange={onPageChange}
          onCloseNotification={onCloseNotification}
        />
      </div>
    </>
  )
}

CommentsOpenEnded.propTypes = {
  specialMode: PropTypes.string,
  surveyData: PropTypes.object,
  reportId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  apiKey: PropTypes.object,
  questionId: PropTypes.string,
  loadRejectedCS: PropTypes.string,
  sentimentOptions: PropTypes.object,
  reportParams: PropTypes.object,
  reportVirtual: PropTypes.object,
  reportSchema: PropTypes.array,
  reports: PropTypes.array,
  isShowHiddenFilters: PropTypes.bool,
  isAdvancedFiltersDisabled: PropTypes.bool,
  isQAModeDisabled: PropTypes.bool,
  isSpecialsSortingDisabled: PropTypes.bool,
  isAiAssistantsOECodingDisabled: PropTypes.bool,
  onFetch: PropTypes.func,
  onTagsFetch: PropTypes.func,
  onFetchSchema: PropTypes.func,
  onFetchApiKey: PropTypes.func,
  onCreateFilter: PropTypes.func,
  onUpdateSchema: PropTypes.func,
  onUpdateParams: PropTypes.func,
  onTranslationChange: PropTypes.func,
  onQuestionsListChange: PropTypes.func,
  onSelectedQuestionChange: PropTypes.func,
  onQAModeChange: PropTypes.func
}

CommentsOpenEnded.displayName = 'CommentsOpenEnded'

export default CommentsOpenEnded
